import { reactive, ref, shallowRef, type VNode } from "vue"
import { renderQuestion } from "./wizard-renderer"

export type WizardComponentRenderer<Result> = (params: WizardRendererParams<Result>) => VNode

export type WizardRendererParams<Result> = {
    wizard: Wizard<Result>
    component: WizardComponent<Result>
}

export type WizardFinishedCallback<Result> = (params: { wizard: Wizard<Result>; result: Result }) => void

/**
 * A component that is shown in the wizard. This is available to the component itself through the render function,
 * so it can call the next() function and return further information about its completion to the wizard (like the
 * answer label shown in the breadcrumb).
 */
export interface WizardComponent<Result> {
    /**
     * The label of the question. This is shown to the user.
     */
    label: string

    /**
     * The label to the question that is shown in the breadcrumb.
     */
    answerLabel: string | VNode | null

    /**
     * The component that is shown to the user.
     */
    render: WizardComponentRenderer<Result>

    /**
     * Go back in the stack to this question.
     */
    goBack: () => void
}

export interface WizardQuestion<Result> extends WizardComponent<Result> {
    /**
     * The possible answers to the question. This is shown to the user as buttons.
     */
    answers: WizardAnswer<Result>[]

    /**
     * When an answer was selected, it gets stored here. This is used to go back in the wizard and to show the
     * breadcrumb at the top of the wizard ui.
     */
    selectedAnswer: WizardAnswer<Result> | null
}

export interface Wizard<Result> {
    /**
     * Breadcrumb of already answered questions. This is used to go back in the wizard and to show the
     * breadcrumb at the top of the wizard ui.
     */
    questionStack: WizardComponent<Result>[]

    /**
     * The question that is currently shown in the wizard.
     */
    currentComponent: WizardComponent<Result> | null

    /**
     * The result of the wizard. This is the final result that is returned when the wizard is finished.
     */
    model: Result

    /**
     * Add a new question to the wizard and mark it as the current question.
     */
    question(label: string, options: WizardAnswer<Result>[]): void

    /**
     * Create a component (and don't add it to the wizard yet).
     */
    createQuestion(label: string, options: WizardAnswer<Result>[]): WizardQuestion<Result>

    /**
     * Create a component.
     */
    component(label: string, render: WizardComponentRenderer<Result>): WizardComponent<Result>

    /**
     * Add a new question to the wizard and mark it as the current question.
     */
    append(question: WizardComponent<Result>): void

    /**
     * Create a new answer.
     */
    answer(label: string, next: () => WizardComponent<Result> | void, disabled?: boolean): WizardAnswer<Result>

    /**
     * Finish and resolve the wizard
     */
    finish(result?: Result): void

    /**
     * Register a callback that is called when the wizard is finished.
     */
    onFinish(callback: WizardFinishedCallback<Result>): void

    /**
     * Set the answer label of the current component. This is shown in the breadcrumb.
     */
    setCurrentAnswerLabel(label: string | VNode | null): void
}

export interface WizardAnswer<Result> {
    /**
     * The label of the answer. This is shown to the user.
     */
    label: string

    /**
     * The question that this answer belongs to. Used to set the selected answer on the question.
     */
    question: WizardQuestion<Result>

    /**
     * The next question or component that is shown when the answer is selected. This can be a question or a
     * component.
     */
    next: () => WizardComponent<Result> | void

    /**
     * Whether selecting this answer is disabled.
     */
    disabled: boolean

    /**
     * The function that is called when the answer is selected. This is used to mark the answer as
     * selected and to show the next question or component.
     */
    select: () => void
}

export function createWizard<Result>(initialModel: Result): Wizard<Result> {
    /**
     * The stack of already answered questions. This is used to go back in the wizard and to show the
     * breadcrumb at the top of the wizard ui.
     */
    const questionStack = reactive<WizardComponent<Result>[]>([])

    /**
     * The question that is currently shown in the wizard.
     */
    const currentComponent = ref<WizardComponent<Result> | null>(null)

    /**
     * The result of the wizard. This is the final result that is returned when the wizard is finished.
     */
    const model = shallowRef<Result>(initialModel)

    /**
     * Multiple listeners can be registered to be called when the wizard is finished.
     */
    const onFinishListeners: WizardFinishedCallback<Result>[] = []

    /**
     * Add a new question to the wizard and mark it as the current question.
     */
    function question(label: string, answers: WizardAnswer<Result>[]) {
        append(createQuestion(label, answers))
    }

    /**
     * Create a new question and return it. This is used to create a question that is not yet added to the
     * wizard.
     */
    function createQuestion(label: string, answers: WizardAnswer<Result>[]) {
        const question: WizardQuestion<Result> = {
            label,
            answers,
            answerLabel: null,
            selectedAnswer: null,
            render: () => renderQuestion({ wizard, component: question }),
            goBack: () => goBack(question),
        }

        answers.map(answer => {
            answer.question = question

            return answer
        })

        return question
    }

    function goBack(component: WizardComponent<Result>) {
        const index = questionStack.indexOf(component)

        if (index === -1) {
            return
        }

        // remove all questions after the current question, including the current question
        questionStack.splice(index)

        // set the current question to the last question in the stack
        currentComponent.value = component
    }

    function component(label: string, render: WizardComponentRenderer<Result>) {
        const newComponent: WizardComponent<Result> = {
            label,
            answerLabel: null,
            render: () => render({ wizard, component: newComponent }),
            goBack: () => goBack(newComponent),
        }

        return newComponent
    }

    function append(question: WizardComponent<Result>) {
        if (currentComponent.value) {
            questionStack.push(currentComponent.value)
        }

        currentComponent.value = question
    }

    /**
     * Add a new answer to the current question.
     */
    function answer(label: string, next: () => WizardComponent<Result> | void, disabled: boolean = false): WizardAnswer<Result> {
        const answer: WizardAnswer<Result> = {
            label,
            next,
            disabled,
            question: null!,
            select: () => {
                if (answer.disabled) {
                    return
                }

                // answer.question is set in the question function, so this always has a value
                answer.question.selectedAnswer = answer
                answer.question.answerLabel = label

                const nextComponent = next()

                if (!nextComponent) {
                    // finish the wizard
                    console.log("No next component given for answer", label)
                    return
                }

                append(nextComponent)
            },
        }

        return answer
    }

    /**
     * Finish and resolve the wizard
     */
    function finish(result: Result | undefined = undefined): void {
        if (result) {
            model.value = result
        }

        onFinishListeners.forEach(callback => {
            callback({ wizard, result: model.value })
        })
    }

    /**
     * Register a callback that is called when the wizard is finished.
     */
    function onFinish(callback: WizardFinishedCallback<Result>) {
        onFinishListeners.push(callback)
    }

    /**
     * Set the answer label of the current component. This is shown in the breadcrumb.
     */
    function setCurrentAnswerLabel(label: string | VNode | null) {
        if (currentComponent.value) {
            currentComponent.value.answerLabel = label
        }
    }

    const wizard: Wizard<Result> = reactive({
        questionStack,
        currentComponent,
        model,

        question,
        createQuestion,
        component,
        append,
        answer,
        finish,
        onFinish,
        setCurrentAnswerLabel,
    })

    return wizard
}

export function isWizardQuestion<T>(value: WizardComponent<T> | void): value is WizardQuestion<T> {
    return (value as WizardQuestion<T>).label !== undefined
}
